// Accessible autosuggest (an ARIA combobox), shared by the Lookup Tool parish finder and
// the church/organisation field on the registration and account forms.
//
// Progressive enhancement over server-rendered results: something else (Datastar on the
// Lookup Tool) still owns fetching + morphing the result rows; this module adds the
// combobox behaviour — floating the panel, opening/closing it, keyboard navigation
// (Up/Down/Home/End/Enter/Esc), active-option highlighting and all the ARIA wiring.
//
// The designer's <form>/<input> markup is left untouched: every ARIA attribute is set
// here, at runtime, because the combobox semantics are only true once this behaviour
// exists. Without JS the panel renders in flow and the option links work natively.
//
// Two selection modes:
//   navigate (default) — Enter/click follows the option's <a href>. The Lookup Tool.
//   select             — Enter/click writes the option's value into a hidden input and
//                        its label into the visible one, then closes. The church field.
//
// Configured per instance from data attributes on the root, so nothing here knows what a
// parish or a church is:
//
//   data-combobox                 the hook itself
//   data-combobox-input           selector for the visible text input     (required)
//   data-combobox-panel           selector for the floating panel         (required)
//   data-combobox-listbox         selector for the role="listbox"         (required)
//   data-combobox-mode            "navigate" (default) | "select"
//   data-combobox-value-input     selector for the hidden input to fill   (select mode)
//
// and on each row: data-combobox-value / data-combobox-label (select mode).

const COMBOBOX_ROOTS = "[data-combobox]";

// Utility classes toggled on the keyboard-active option — matched to the CSS :hover look
// (grey wash + purple left rule) so pointer and keyboard agree.
const ACTIVE_CLASSES = ["bg-off-white", "shadow-[inset_3px_0_0_#3F0F4D]"];

const enhanceCombobox = (root) => {
  const settings = root.dataset;
  const searchInput = settings.comboboxInput && root.querySelector(settings.comboboxInput);
  const resultsPanel = settings.comboboxPanel && root.querySelector(settings.comboboxPanel);
  const optionsListbox = settings.comboboxListbox && root.querySelector(settings.comboboxListbox);

  // Bailing quietly here is how this used to fail: no enhancement, no error, and a panel
  // that just renders in flow like the no-JS path. Say so instead — a root marked
  // data-combobox whose parts don't resolve is a markup bug, not a state to tolerate.
  if (!searchInput || !resultsPanel || !optionsListbox) {
    console.warn("combobox: root found but could not resolve", {
      root,
      input: settings.comboboxInput,
      panel: settings.comboboxPanel,
      listbox: settings.comboboxListbox,
    });
    return;
  }

  const selectionMode = settings.comboboxMode || "navigate";
  const valueInput = settings.comboboxValueInput
    ? document.querySelector(settings.comboboxValueInput)
    : null;

  let activeId = null;

  // Options currently eligible to show: role="option" inside a group Datastar hasn't
  // hidden (data-show). Tested via the group's own computed display, so it holds even
  // while the panel itself is hidden (which is how open() can decide whether there's
  // anything worth opening for).
  const selectableOptions = () =>
    Array.from(optionsListbox.querySelectorAll('[role="option"]')).filter((option) => {
      const group = option.closest("[data-show]");
      return !group || getComputedStyle(group).display !== "none";
    });

  // Rows the panel would actually show: selectable options PLUS any non-selectable notice —
  // the greyed "current parish" row in compare mode, and [data-combobox-notice] messages
  // ("no parishes found", an unmatched postcode, a geocode failure). Used only to decide
  // whether opening the panel is worthwhile. Notices have to count: without them a search
  // that finds nothing leaves zero rows, the panel stays shut and the field looks dead
  // rather than explaining itself. Keyboard nav and selection stay on selectableOptions().
  const renderedRows = () =>
    Array.from(
      optionsListbox.querySelectorAll(
        '[role="option"], [aria-disabled="true"], [data-combobox-notice]',
      ),
    ).filter((row) => {
      const group = row.closest("[data-show]");
      return !group || getComputedStyle(group).display !== "none";
    });

  const setActive = (id) => {
    activeId = id;
    optionsListbox.querySelectorAll('[role="option"]').forEach((option) => {
      option.classList[option.id === id ? "add" : "remove"](...ACTIVE_CLASSES);
    });
    if (id) {
      searchInput.setAttribute("aria-activedescendant", id);
      document.getElementById(id)?.scrollIntoView({ block: "nearest" });
    } else {
      searchInput.removeAttribute("aria-activedescendant");
    }
  };

  const open = () => {
    if (renderedRows().length === 0) return;
    resultsPanel.hidden = false;
    searchInput.setAttribute("aria-expanded", "true");
  };

  const close = () => {
    resultsPanel.hidden = true;
    searchInput.setAttribute("aria-expanded", "false");
    setActive(null);
  };

  const move = (delta) => {
    const options = selectableOptions();
    if (options.length === 0) return;
    open();
    const current = options.findIndex((option) => option.id === activeId);
    const next =
      current === -1
        ? delta > 0
          ? 0
          : options.length - 1
        : (current + delta + options.length) % options.length;
    setActive(options[next].id);
  };

  // Selection. In navigate mode the option is a link and we follow it, exactly as before.
  // In select mode we write the pick into the form and tell the page about it — the module
  // itself stays ignorant of what the value means.
  const choose = (option) => {
    if (!option) return;

    if (selectionMode !== "select") {
      const href = option.querySelector("a[href]")?.getAttribute("href");
      if (href) window.location.href = href;
      return;
    }

    const value = option.dataset.comboboxValue ?? "";
    const label = option.dataset.comboboxLabel ?? option.textContent.trim();

    if (valueInput) valueInput.value = value;
    searchInput.value = label;
    close();

    // Not an `input` event on the search box: that would look like typing and set the
    // fetch/clear cycle off again, undoing the selection we just made.
    root.dispatchEvent(
      new CustomEvent("combobox:select", { bubbles: true, detail: { value, label } }),
    );
  };

  // Take option links out of the tab order (the combobox owns focus). Re-applied after
  // each morph brings fresh result rows in.
  const captureOptionLinks = () => {
    optionsListbox.querySelectorAll('[role="option"] a[href]').forEach((link) => {
      link.setAttribute("tabindex", "-1");
    });
  };

  // ---- Wire it up --------------------------------------------------------
  root.classList.add("is-enhanced");
  // z-30 so the panel clears the parish page's sticky chart legend (.tabpanel-sticky-item,
  // z-20) which follows it in the document and would otherwise win the tie.
  resultsPanel.classList.add("absolute", "left-0", "right-0", "top-full", "z-30");
  searchInput.setAttribute("role", "combobox");
  searchInput.setAttribute("aria-autocomplete", "list");
  searchInput.setAttribute("aria-haspopup", "listbox");
  if (optionsListbox.id) searchInput.setAttribute("aria-controls", optionsListbox.id);
  captureOptionLinks();
  close();

  // There is nowhere to submit to — picking a result follows its link. Left un-cancelled, the
  // search button (where one exists) and Enter-with-no-option-active both reload the page with
  // ?q=, losing scroll position and closing the panel. Scoped to the root, so a combobox that
  // sits INSIDE a real form (the church field on registration) never has its host form touched.
  root.querySelector("form")?.addEventListener("submit", (event) => event.preventDefault());

  searchInput.addEventListener("focus", open);

  searchInput.addEventListener("input", () => {
    setActive(null);

    // Typing after a pick means the stored value no longer describes what's in the box.
    // Drop it, or they'd submit a church code that doesn't match the name they can see.
    if (selectionMode === "select" && valueInput && valueInput.value !== "") {
      valueInput.value = "";
      root.dispatchEvent(new CustomEvent("combobox:clear", { bubbles: true }));
    }

    // Let the result rows morph in, then re-evaluate.
    requestAnimationFrame(() => {
      if (document.activeElement !== searchInput) return;
      renderedRows().length ? open() : close();
    });
  });

  searchInput.addEventListener("keydown", (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        if (resultsPanel.hidden) break;
        event.preventDefault();
        setActive(selectableOptions()[0]?.id ?? null);
        break;
      case "End": {
        if (resultsPanel.hidden) break;
        event.preventDefault();
        const options = selectableOptions();
        setActive(options[options.length - 1]?.id ?? null);
        break;
      }
      case "Enter":
        if (!resultsPanel.hidden && activeId) {
          event.preventDefault();
          choose(document.getElementById(activeId));
        }
        break;
      case "Escape":
        if (!resultsPanel.hidden) {
          event.preventDefault();
          close();
        }
        break;
    }
  });

  // Result rows are morphed in place — re-capture links and refresh.
  new MutationObserver(() => {
    captureOptionLinks();
    setActive(null);
    if (document.activeElement === searchInput && renderedRows().length) open();
    else if (renderedRows().length === 0) close();
  }).observe(optionsListbox, { childList: true, subtree: true });

  // Whole-row click (works for morphed rows via delegation). Pointer highlighting is pure
  // CSS :hover — see the hover: utilities on each option — so it clears itself on mouse-out
  // and never fights the keyboard-active state.
  optionsListbox.addEventListener("click", (event) => {
    const option = event.target.closest('[role="option"]');
    if (option && optionsListbox.contains(option)) {
      event.preventDefault();
      choose(option);
    }
  });

  // Close on outside click or focus leaving the combobox.
  document.addEventListener("click", (event) => {
    if (!root.contains(event.target)) close();
  });
  root.addEventListener("focusout", (event) => {
    if (!root.contains(event.relatedTarget)) close();
  });
};

// site.js ships on every page, so this self-guards: no roots, no work.
document.querySelectorAll(COMBOBOX_ROOTS).forEach(enhanceCombobox);
